// Логика формы предпросмотра одного черновика рейса (без React) — владелец agent-parser.
import type { Currency, PriceScope } from '../../types';
import { toUtcMillis } from '../../core';
import { lookupTz, type ParsedFlightDraft } from '../../parser';
import { createTranslator, type Translate } from '../../i18n/translations';
import type { AiDraft } from './api';

export interface DraftForm {
  include: boolean;
  from: string;
  to: string;
  fromTz: string;
  toTz: string;
  departLocal: string;
  arriveLocal: string;
  carrier: string;
  flightNumber: string;
  amount: string;
  currency: Currency;
  priceScope: PriceScope;
  /** какие поля не распознал парсер — для подсветки, не пересчитывается при правках */
  missing: string[];
  notes: string[];
}

const IATA_RE = /^[A-Z]{3}$/;

/** Черновик парсера -> редактируемая форма (изначально отмечена «добавить», если всё распознано). */
export function draftToForm(d: ParsedFlightDraft): DraftForm {
  return {
    include: d.missing.length === 0,
    from: d.from,
    to: d.to,
    fromTz: d.fromTz,
    toTz: d.toTz,
    departLocal: d.departLocal,
    arriveLocal: d.arriveLocal,
    carrier: d.carrier,
    flightNumber: d.flightNumber ?? '',
    amount: d.priceMinor !== null ? String(d.priceMinor / 100) : '',
    currency: d.currency,
    priceScope: 'one_way',
    missing: d.missing,
    notes: d.notes,
  };
}

/**
 * Черновик сервера -> редактируемая форма. Сервер почти всегда отдаёт
 * fromTz/toTz пустыми (SPEC.md) — таймзоны находятся здесь же, через
 * справочник аэропортов парсера, и заодно дополняют missing: пропавшую
 * таймзону сервер в своём missing не перечисляет (там только
 * from|to|departLocal|arriveLocal|carrier|price), клиент обязан подсветить её сам.
 */
export function aiDraftToForm(d: AiDraft): DraftForm {
  const fromTz = d.from ? lookupTz(d.from) : '';
  const toTz = d.to ? lookupTz(d.to) : '';

  const missing = d.missing.filter((m) => m !== 'fromTz' && m !== 'toTz');
  if (!fromTz) missing.push('fromTz');
  if (!toTz) missing.push('toTz');

  return {
    include: missing.length === 0,
    from: d.from,
    to: d.to,
    fromTz,
    toTz,
    departLocal: d.departLocal,
    arriveLocal: d.arriveLocal,
    carrier: d.carrier,
    flightNumber: d.flightNumber,
    amount: d.priceMinor !== null ? String(d.priceMinor / 100) : '',
    currency: d.currency,
    priceScope: 'one_way',
    missing,
    notes: d.notes,
  };
}

export interface DraftFormErrors {
  from?: string;
  to?: string;
  departLocal?: string;
  arriveLocal?: string;
  amount?: string;
  /** предупреждение — не блокирует сохранение */
  tzWarning?: string;
}

/** Валидация перед сохранением. Неизвестная таймзона — предупреждение, не блокировка. */
export function validateDraftForm(f: DraftForm, t: Translate = createTranslator('ru')): DraftFormErrors {
  const errs: DraftFormErrors = {};
  if (!IATA_RE.test(f.from.toUpperCase())) errs.from = t('error.iata');
  if (!IATA_RE.test(f.to.toUpperCase())) errs.to = t('error.iata');
  if (!f.departLocal) errs.departLocal = t('error.departDateRequired');
  if (!f.arriveLocal) errs.arriveLocal = t('error.arriveDateRequired');

  if (!f.fromTz || !f.toTz) {
    errs.tzWarning = t('warn.tzMissingParse');
  } else if (f.departLocal && f.arriveLocal) {
    const depMs = toUtcMillis(f.departLocal, f.fromTz);
    const arrMs = toUtcMillis(f.arriveLocal, f.toTz);
    if (depMs !== null && arrMs !== null && arrMs <= depMs) {
      errs.arriveLocal = t('error.arriveBeforeDepart');
    }
  }

  const amountNum = parseFloat(f.amount.replace(',', '.'));
  if (f.amount.trim() === '' || Number.isNaN(amountNum) || amountNum < 0) {
    errs.amount = t('error.priceNegative');
  }

  return errs;
}

/** Есть ли блокирующие ошибки (предупреждение о таймзоне не блокирует). */
export function hasBlockingErrors(errs: DraftFormErrors): boolean {
  // Перевозчик не блокирует: он необязателен и в ручной форме. Ненайденным он
  // всё равно останется подсвечен как «не распознано».
  return Boolean(errs.from || errs.to || errs.departLocal || errs.arriveLocal || errs.amount);
}
